import { getTakeoverVariantList } from 'modules/experiments/ducks/selectors';
import { useMessageTypeIdQuery, useTemplateValidationPerLocale } from 'modules/experiments/hooks';
import { getTakeoverExperimentLocale } from 'modules/experiments/takeover/ducks/selectors';
import {
  ValidationContextStateValue,
  VariantTemplateError,
  VariantTemplateErrorTuple,
} from 'modules/experiments/types';
import { aggregateErrors } from 'modules/templateEditor/common';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { Nullable } from 'utils/tsHelpers';
import { ValidationContext } from './ValidationContext';

const validationKey = (templateId: number, locale: Nullable<string>) => `${templateId}-${locale}`;
const hasErrorsOrWarnings = (value: Nullable<VariantTemplateError>) => {
  return Object.values(value || {}).some((arr) => arr.length > 0);
};

export const ValidationContextProvider: React.FC<
  React.PropsWithChildren<{
    controlVariantId: number;
  }>
> = ({ controlVariantId, children }) => {
  const variantList = useSelector(getTakeoverVariantList);
  const {
    activeTemplateLocales,
    templateDefaultLocale: { tag: defaultExperimentLocale = null },
  } = useLocalesQueryContext();
  const activeExperimentLocaleList = activeTemplateLocales.length
    ? activeTemplateLocales
    : [{ tag: null }];
  const selectedLocale = useSelector(getTakeoverExperimentLocale);
  const currentLocaleTag = selectedLocale || defaultExperimentLocale;

  const fetchTemplateMessageTypeId = useMessageTypeIdQuery();
  const queryTemplateValidation = useTemplateValidationPerLocale();

  const [variantCardTemplateErrors, setVariantCardTemplateErrors] = useState(
    new Map<string, Nullable<VariantTemplateError>>(),
  );
  const [currentLocalesTemplateError, setCurrentLocalesTemplateError] = useState<
    ValidationContextStateValue['currentLocalesTemplateError']
  >([]);
  const [messageTypeId, setMessageTypeId] = useState(-1);
  const [localesWithError, setLocalesWithError] = useState<string[]>([]);

  const hasVariants = variantList.length > 0;
  const canFetchMessageTypeId = hasVariants && controlVariantId > 0;
  const canStartValidating = messageTypeId > 0 && canFetchMessageTypeId;

  const getVariantName = (variantId: number) =>
    variantList.find(({ id }) => variantId === id)?.name ?? '';

  const getTemplateErrors = useCallback(
    async (templateId: number, locale: Nullable<string>) => {
      const variantName = getVariantName(templateId);
      const validation = await queryTemplateValidation(
        templateId,
        variantName,
        messageTypeId,
        locale,
      );
      return validation ? aggregateErrors(locale || '', validation) : null;
    },
    [queryTemplateValidation, getVariantName, messageTypeId],
  );

  // Re-valuates template errors for all locales for a given variant id
  const updateTemplateErrorsForAllVariantLocales = useCallback(
    async (variantId: number) => {
      activeExperimentLocaleList.forEach(async ({ tag: locale }) => {
        const templateErrors = await getTemplateErrors(variantId, locale);

        setVariantCardTemplateErrors((prevMap) => {
          const updatedMap = new Map(prevMap);
          updatedMap.set(validationKey(variantId, locale), templateErrors);
          return updatedMap;
        });
      });
    },
    [getTemplateErrors, activeExperimentLocaleList],
  );

  // Deletes the template errors for all locales for a given variant id
  const deleteTemplateErrorsForAllVariantLocales = useCallback(
    (variantId: number) => {
      setVariantCardTemplateErrors((prevMap) => {
        const updatedMap = new Map(prevMap);

        activeExperimentLocaleList.forEach(({ tag: locale }) => {
          updatedMap.delete(validationKey(variantId, locale));
        });
        return updatedMap;
      });
    },
    [activeExperimentLocaleList],
  );

  /**
   * Setting the validation data for all variants
   * We will be doing this once we have the variant list available
   */
  useEffect(() => {
    if (!canStartValidating) return;

    const populateValidationMap = async () => {
      const initialValidationMap = new Map<string, Nullable<VariantTemplateError>>();

      const initialValidationMapDataPromise = variantList.map(async ({ id: templateId }) => {
        // Validating all locales for a given template
        const localeTemplateValidationPromise = activeExperimentLocaleList.map(
          async ({ tag: locale }) => {
            const templateErrors = await getTemplateErrors(templateId, locale);
            return [validationKey(templateId, locale), templateErrors] as VariantTemplateErrorTuple;
          },
        );
        return Promise.allSettled(localeTemplateValidationPromise);
      });

      // Validation data of all locale and variant's template of an experiment
      await Promise.allSettled(initialValidationMapDataPromise).then((results) => {
        results.forEach((promiseSettledResult) => {
          if (promiseSettledResult.status === 'fulfilled') {
            promiseSettledResult.value.forEach((innerPromiseSettledResult) => {
              if (innerPromiseSettledResult.status === 'fulfilled') {
                const [key, value] = innerPromiseSettledResult.value;
                initialValidationMap.set(key, value);
              }
            });
          }
        });
      });

      setVariantCardTemplateErrors(initialValidationMap);
    };

    populateValidationMap();
  }, [canStartValidating, messageTypeId, activeExperimentLocaleList?.length, hasVariants]);

  /**
   * Fetches the message type id of the control
   * which is used to validate the templates
   */
  useEffect(() => {
    if (!canFetchMessageTypeId) return;

    const getMessageTypeId = async () => {
      const id = (await fetchTemplateMessageTypeId(controlVariantId, currentLocaleTag)) || -1;
      setMessageTypeId(id);
    };

    getMessageTypeId();
  }, [canFetchMessageTypeId, currentLocaleTag, controlVariantId]);

  /**
   * A list of current template errors with the corresponding
   * variant info. We would be using this to populate the global
   * error accordion
   */
  useEffect(() => {
    if (!canStartValidating || !variantCardTemplateErrors.size) return;

    const newCurrentLocalesTemplateError: ValidationContextStateValue['currentLocalesTemplateError'] =
      {};

    variantList.forEach(({ id: variantId }) => {
      newCurrentLocalesTemplateError[variantId] =
        variantCardTemplateErrors.get(validationKey(variantId, currentLocaleTag)) || null;
    });

    setCurrentLocalesTemplateError(newCurrentLocalesTemplateError);
  }, [canStartValidating, currentLocaleTag, variantCardTemplateErrors]);

  /**
   * We want to maintain a list of locales which has errors in them
   * We do this by monitoring the variantCardTemplateErrors map
   */
  useEffect(() => {
    if (!canStartValidating || !variantCardTemplateErrors.size) return;

    const updatedLocaleTags = new Set<string>();
    variantCardTemplateErrors.forEach((value, key) => {
      if (hasErrorsOrWarnings(value)) {
        const localeTag = key.split('-').slice(1).join('-');
        updatedLocaleTags.add(localeTag);
      }
    });
    setLocalesWithError([...updatedLocaleTags]);
  }, [canStartValidating, variantCardTemplateErrors]);

  const context: ValidationContextStateValue = {
    deleteTemplateErrorsForAllVariantLocales,
    updateTemplateErrorsForAllVariantLocales,
    localesWithError,
    currentLocalesTemplateError,
    experimentHasErrors: localesWithError.length > 0,
  };

  return <ValidationContext.Provider value={context}>{children}</ValidationContext.Provider>;
};
